import React, { useState, useEffect } from 'react';
import { Plus, Calendar, Heading as Bread, Wine, X } from 'lucide-react';
import { collection, addDoc, serverTimestamp, doc, getDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { saveNeedsFromNotes } from '../../utils/needsService';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { SainteCeneReport } from '../../types/sainteCeneReport';
import { WorshipReportSelect } from '../ui/WorshipReportSelect';
import { WorshipReport } from '../../types/worshipReport';

interface SainteCeneFormProps {
  onClose: () => void;
  onSuccess: () => void;
}

export default function SainteCeneForm({ onClose, onSuccess }: SainteCeneFormProps) {
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState<Omit<SainteCeneReport, 'id' | 'painsRestantes' | 'vinsRestantes'>>({
    date: '',
    worshipReportId: '',
    painsPreparees: 0,
    vinsPreparees: 0,
    painsDistribuees: 0,
    vinsDistribuees: 0,
    needsNotes: ''
  });

  // Auto-populate date from selected worship report
  useEffect(() => {
    const fetchWorshipReportData = async () => {
      if (!formData.worshipReportId) {
        setFormData(prev => ({ ...prev, date: '' }));
        return;
      }

      try {
        const worshipReportRef = doc(db, 'worshipReports', formData.worshipReportId);
        const worshipReportSnap = await getDoc(worshipReportRef);
        
        if (worshipReportSnap.exists()) {
          const worshipReportData = worshipReportSnap.data() as WorshipReport;
          setFormData(prev => ({
            ...prev,
            date: worshipReportData.date
          }));
        }
      } catch (error) {
        console.error('Error fetching worship report:', error);
      }
    };

    fetchWorshipReportData();
  }, [formData.worshipReportId]);

  // Calcul automatique des quantités restantes
  const painsRestantes = Math.max(0, formData.painsPreparees - formData.painsDistribuees);
  const vinsRestantes = Math.max(0, formData.vinsPreparees - formData.vinsDistribuees);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (!formData.worshipReportId) {
        throw new Error('Veuillez sélectionner le rapport de culte à annexer');
      }
      
      const reportData: Omit<SainteCeneReport, 'id'> = {
        ...formData,
        painsRestantes,
        vinsRestantes,
        createdAt: new Date(),
        createdBy: user?.id,
      };

      const docRef = await addDoc(collection(db, 'sainteCeneReports'), {
        ...reportData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // Sauvegarder les besoins si présents
      if (formData.needsNotes) {
        await saveNeedsFromNotes({
          reportId: docRef.id,
          reportType: 'sainteCene',
          department: 'Sainte Cène',
          needsNotes: formData.needsNotes,
          reportDate: formData.date
        });
      }

      showNotification('Rapport Sainte Cène créé avec succès', 'success');
      onSuccess();
    } catch (error) {
      console.error('Erreur lors de la création du rapport:', error);
      showNotification('Erreur lors de la création du rapport', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof typeof formData, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-800 flex items-center">
          <Wine className="mr-2 text-[#00665C]" />
          Nouveau Rapport Sainte Cène
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="space-y-6">
        {/* Rapport de culte à annexer */}
        <div>
          <WorshipReportSelect
            value={formData.worshipReportId || ''}
            onChange={(value) => handleInputChange('worshipReportId', value)}
            required
              departmentCollection="sainteCeneReports"
          />
        </div>
        
        {/* Date */}
        <div>
          <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
            <Calendar className="w-4 h-4 mr-2" />
            Date
          </label>
          <input
            type="date"
            value={formData.date}
            onChange={(e) => handleInputChange('date', e.target.value)}
           disabled={true}
            className={`w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00665C] focus:border-[#00665C] ${
             'bg-gray-100 cursor-not-allowed'
            }`}
          />
        </div>

        {/* Quantités Préparées */}
        <div className="bg-blue-50 p-4 rounded-lg">
          <h3 className="font-semibold text-blue-800 mb-4 flex items-center">
            <Plus className="w-4 h-4 mr-2" />
            Quantité de Sainte Cène Préparée
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                <Bread className="w-4 h-4 mr-2" />
                Pains
              </label>
              <input
                type="number"
                min="0"
                value={formData.painsPreparees}
                onChange={(e) => handleInputChange('painsPreparees', parseInt(e.target.value) || 0)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00665C] focus:border-[#00665C]"
                required
              />
            </div>
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                <Wine className="w-4 h-4 mr-2" />
                Vins
              </label>
              <input
                type="number"
                min="0"
                value={formData.vinsPreparees}
                onChange={(e) => handleInputChange('vinsPreparees', parseInt(e.target.value) || 0)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00665C] focus:border-[#00665C]"
                required
              />
            </div>
          </div>
        </div>

        {/* Quantités Distribuées */}
        <div className="bg-green-50 p-4 rounded-lg">
          <h3 className="font-semibold text-green-800 mb-4">Quantité Distribuée</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                <Bread className="w-4 h-4 mr-2" />
                Pains
              </label>
              <input
                type="number"
                min="0"
                max={formData.painsPreparees}
                value={formData.painsDistribuees}
                onChange={(e) => handleInputChange('painsDistribuees', parseInt(e.target.value) || 0)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00665C] focus:border-[#00665C]"
                required
              />
            </div>
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                <Wine className="w-4 h-4 mr-2" />
                Vins
              </label>
              <input
                type="number"
                min="0"
                max={formData.vinsPreparees}
                value={formData.vinsDistribuees}
                onChange={(e) => handleInputChange('vinsDistribuees', parseInt(e.target.value) || 0)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00665C] focus:border-[#00665C]"
                required
              />
            </div>
          </div>
        </div>

        {/* Quantités Restantes (calculées automatiquement) */}
        <div className="bg-orange-50 p-4 rounded-lg">
          <h3 className="font-semibold text-orange-800 mb-4">Quantité Restantes</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                <Bread className="w-4 h-4 mr-2" />
                Pains
              </label>
              <div className="w-full p-3 bg-gray-100 border border-gray-300 rounded-lg text-gray-700 font-semibold">
                {painsRestantes.toString().padStart(2, '0')}
              </div>
            </div>
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                <Wine className="w-4 h-4 mr-2" />
                Vins
              </label>
              <div className="w-full p-3 bg-gray-100 border border-gray-300 rounded-lg text-gray-700 font-semibold">
                {vinsRestantes.toString().padStart(2, '0')}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Besoins */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="font-semibold text-gray-800 mb-4">Liste des besoins</h3>
        <textarea
          placeholder="Listez les besoins du département (un besoin par ligne)&#10;Exemple:&#10;- Pains supplémentaires pour dimanche prochain&#10;- Vins de qualité supérieure&#10;- Nappes pour les tables"
          value={formData.needsNotes || ''}
          onChange={(e) => setFormData(prev => ({ ...prev, needsNotes: e.target.value }))}
          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#00665C]"
          rows={4}
        />
        <p className="mt-1 text-xs text-gray-500">
          Entrez un besoin par ligne. Ces besoins seront ajoutés à la liste de suivi des besoins du département.
        </p>
      </div>

      <div className="flex justify-end space-x-3 mt-8 pt-6 border-t">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
        >
          Annuler
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-6 py-2 bg-[#00665C] text-white rounded-lg hover:bg-[#004d45] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isSubmitting ? 'Création...' : 'Créer le Rapport'}
        </button>
      </div>
    </form>
  );
}